// ============================================
// Frontend Type Definitions
// Mirrors backend types for all 5 PDF forms
// Updated 2026-01-29
// ============================================

// ============================================
// Common Member Information (used in ALL forms)
// ============================================
export interface MemberInfo {
  firstName: string;
  lastName: string;
  dateOfBirth: string; // ISO format
  medicalId: string; // Medi-Cal ID/CIN
  phone: string;
  email: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
}

// ============================================
// ECM ROI Data (ECM_ROI.pdf)
// ============================================
export interface ROIData {
  // Authorizing organization
  ecmProviderName?: string;
  // Information to be released (checkboxes)
  informationToRelease: {
    carePlans: boolean;
    medicalHistory: boolean;
    medications: boolean;
    mentalHealth: boolean;
    substanceUse: boolean;
    hivAids: boolean;
    hospitalizationER: boolean; // Hospitalization / ER records
    socialServices: boolean;
    billing: boolean;
    other: boolean;
    otherDescription?: string;
  };
  // Provider information
  providerFrom: {
    name: string;
    address: string;
    phone: string;
    fax?: string;
  };
  providerTo: {
    name: string;
    address: string;
    phone: string;
    fax?: string;
  };
  // Purpose of disclosure
  purposeOfDisclosure: {
    continuityOfCare: boolean;
    careCoordination: boolean;
    ecmServiceDelivery: boolean;
    carePlanning: boolean;
    eligibility: boolean;
    other: boolean;
    otherDescription?: string;
  };
  // Expiration
  expirationOption: '' | 'one_year' | 'termination' | 'specific_date';
  specificExpirationDate?: string;
  // Special consents (initials)
  mentalHealthInitial?: string;
  sudInitial?: string;
  hivInitial?: string;
  // Witness (optional)
  witnessName?: string;
  witnessDate?: string;
  witnessSignature?: string; // base64 image
}

// Separate date parts for DD / MM / YYYY format
export interface DateParts {
  day: string;
  month: string;
  year: string;
}

// ============================================
// Housing Assessment Data (CS_Assessment___Housing_Navigation___Housing_Sustaining.pdf)
// ============================================
export interface HousingData {
  // Member info specific to housing
  managedCarePlan?: string;

  // Referral Source
  referralSource: {
    mcp: boolean;
    ecm: boolean;
    provider: boolean;
    self: boolean;
    other: boolean;
    otherDescription?: string;
  };

  // Assessment info
  assessmentDate: string;
  assessorName: string;
  assessorTitle: string;
  assessorSignatureDate?: string;

  // A. Housing Status (Required)
  currentSituation:
  | 'homeless_unsheltered'
  | 'homeless_sheltered'
  | 'doubled_up'
  | 'imminent_risk'
  | 'at_risk_eviction'
  | 'recently_housed'
  | 'at_risk'
  | 'housed_unstable'
  | 'housed_stable'
  | '';
  currentSituationOther?: string;
  lengthOfSituation: string;

  // Primary Barriers to Housing
  barriers: {
    lowIncome: boolean;
    poorCredit: boolean;
    priorEviction: boolean;
    criminalHistory: boolean;
    disability: boolean;
    behavioralHealth: boolean;
    lackDocuments: boolean;
    languageBarriers: boolean;
    transportationBarriers: boolean;
    financial: boolean;
    credit: boolean;
    rentalHistory: boolean;
    criminal: boolean;
    mentalHealth: boolean;
    substanceUse: boolean;
    family: boolean;
    documentation: boolean;
    other: boolean;
    otherDescription?: string;
  };

  // B. Eligibility & Medical Appropriateness
  healthRelatedNeeds: string;
  relevantDiagnoses: string;
  howHousingHelps: string;
  diagnoses: string[];
  primaryDiagnosis?: string;

  // C. Housing Navigation Assessment
  navigation: {
    housingSearch: boolean;
    applications: boolean;
    documents: boolean;
    accommodation: boolean;
    landlordEngagement: boolean;
    leaseTerms: boolean;
    moveIn: boolean;
    rentalAssistance: boolean;
  };

  // Housing Preferences
  preferences: {
    locationCity: string;
    unitStudio: boolean;
    unit1BR: boolean;
    unit2BR: boolean;
    unitOther: boolean;
    unitOtherDescription?: string;
    accessibilityNone: boolean;
    accessibilityADA: boolean;
    accessibilityOther: boolean;
    accessibilityOtherDescription?: string;
  };

  // Income Sources
  income: {
    ssiSsdi: boolean;
    employment: boolean;
    ga: boolean;
    none: boolean;
    other: boolean;
    otherDescription?: string;
  };

  // D. Housing Sustaining Assessment
  sustaining: {
    newLease: boolean;
    existingLease: boolean;
    informal: boolean;
  };

  // Risk Factors for Housing Loss
  riskFactors: {
    rentUtilities: boolean;
    leaseViolations: boolean;
    conflict: boolean;
    maintainingUnit: boolean;
    missedAppointments: boolean;
    behavioralSymptoms: boolean;
    lackTenancySkills: boolean;
  };

  // Member Needs Support With
  supportNeeds: {
    tenantRights: boolean;
    budgeting: boolean;
    utilitySetup: boolean;
    landlordComm: boolean;
    crisisPrevention: boolean;
    ongoingServices: boolean;
  };

  // E. Risk & Safety Screening
  safety: {
    safetyConcernsYes: boolean;
    safetyConcernsNo: boolean;
    evictionRisk30Yes: boolean;
    evictionRisk30No: boolean;
    repeatedLossYes: boolean;
    repeatedLossNo: boolean;
    mitigationPlan: string;
  };

  // F. Member Goals
  memberGoals: string;
  housingGoals: string;
  shortTermGoals: string;
  longTermGoals: string;

  // G. Service Recommendation
  recommendation: {
    housingNavigation: boolean;
    housingSustaining: boolean;
    both: boolean;
    durationFrequency: string;
    rationale: string;
  };

  // Legacy fields for backward compatibility
  previousEvictions: boolean;
  evictionDetails?: string;
  assessorSignature?: string;
}

// ============================================
// CSS Consent Data (CSS_Consent_Form.pdf)
// ============================================
export interface ConsentData {
  // Minor indicator
  isMinor: boolean;
  // Signing on behalf of beneficiary
  signingOnBehalf: boolean;
  printedName: string;
  relationshipToMember: string;
}

// ============================================
// Care Plan Data (Care_Plan.pdf)
// ============================================
export interface Problem {
  id: string;
  problemName: string;
  description: string;
  priority: 'Low' | 'Medium' | 'High' | 'Critical' | '';
  startDate: string;
  lastReviewed: string;
  resolvedReason: 'Member deceased' | 'Member transitioned to new provider' | 'Member\'s coverage terminated' | 'Other' | '';
  resolvedReasonOther?: string;
  comments: string;
  goals: Goal[];
}

export interface Goal {
  id: string;
  goalName: string;
  description: string;
  startDate: string;
  priority: 'Low' | 'Medium' | 'High' | 'Critical' | '';
  percentComplete: string;
  duration: 'Long Term' | 'Short Term' | '';
  closedDate: string;
  readinessToChange: 'Precontemplation' | 'Contemplation' | 'Preparation' | 'Action' | 'Maintenance' | '';
  closedReason: string;
  comments: string;
  outcome: string;
  targetDate: string;
  interventions: Intervention[];
}

export interface Intervention {
  id: string;
  interventionName: string;
  description: string;
  startDate: string;
  priority: 'Low' | 'Medium' | 'High' | 'Critical' | '';
  targetDate: string;
  type: 'Coordination' | 'Graduation' | '';
  category: 'Behavioral' | 'Medical' | 'Member' | 'Other' | '';
  closedDate: string;
  effective: 'Yes' | 'No' | 'Unknown' | '';
  closedReason: 'Changed' | 'Completed' | 'Member declined' | 'Member disenrolled' | 'No longer achievable' | 'N/A' | 'Member transitioned to new provider' | '';
  comments: string;
}

export interface CarePlanData {
  // Part 1: Provider info
  ecmProvider: {
    name: string;
    npi?: string;
    phone: string;
    address: string;
  };
  leadCareManager: {
    name: string;
    phone: string;
    email: string;
  };
  primaryCareProvider?: {
    name: string;
    phone: string;
    address: string;
  };

  // Part 2: General Information
  ecmTargetGraduationDate: string;
  postGraduationSupports: string;
  communitySupportsReferral: boolean;
  communitySupportsDetails: string;
  dsnpDualEligible: boolean;
  dsnpHealthPlan: string;
  memberComments: string;
  actualGraduationDate: string;

  // Plan dates (legacy)
  planStartDate: string;
  planReviewDate: string;

  // Part 3: Problems and goals
  problems: Problem[];

  // Member participation (legacy)
  memberParticipation: string;
  memberStrengths: string;
  memberPreferences: string;

  // Emergency contacts
  emergencyContact: {
    name: string;
    relationship: string;
    phone: string;
  };
}

// ============================================
// C/Y ECM Assessment Data (cy_ecm_assessment.html)
// Children and Youth Enhanced Care Management Comprehensive Assessment
// ============================================
export interface CYECMAssessmentData {
  // Section 1: Population of Focus and Programs

  // Assessment Tools (13 tools)
  assessmentTools: {
    aces: { used: boolean; yes: boolean; date: string; no: boolean; na: boolean };
    cans: { used: boolean; yes: boolean; date: string; no: boolean; na: boolean };
    psc: { used: boolean; yes: boolean; date: string; no: boolean; na: boolean };
    needs: { used: boolean; yes: boolean; date: string; no: boolean; na: boolean };
    youth: { used: boolean; yes: boolean; date: string; no: boolean; na: boolean };
    dph: { used: boolean; yes: boolean; date: string; no: boolean; na: boolean };
    protective: { used: boolean; yes: boolean; date: string; no: boolean; na: boolean };
    dcfs: { used: boolean; yes: boolean; date: string; no: boolean; na: boolean };
    ccsPatient: { used: boolean; yes: boolean; date: string; no: boolean; na: boolean };
    dds: { used: boolean; yes: boolean; date: string; no: boolean; na: boolean };
    cpsp: { used: boolean; yes: boolean; date: string; no: boolean; na: boolean };
    justice: { used: boolean; yes: boolean; date: string; no: boolean; na: boolean };
    other: boolean;
    otherList: string;
  };

  populationOfFocus: {
    homeless: boolean;
    atRiskED: boolean;
    smiSud: boolean;
    youthCorrectional: boolean;
    ccsCcsWcm: boolean;
    childWelfare: boolean;
    idd: boolean;
    pregnantPostpartum: boolean;
  };
  programs: {
    smhs: boolean;
    dmc: boolean;
    dmcOds: boolean;
    juvenileJustice: boolean;
    ccs: boolean;
    ccsWcm: boolean;
    childWelfare: boolean;
    regionalCenter: boolean;
    localProgram: boolean;
    localProgramList: string;
    other: boolean;
    otherList: string;
    na: boolean;
  };
  consent: {
    date: string;
    verbal: boolean;
    written: boolean;
    cyMember: boolean;
    parentGuardian: boolean;
    dcfs: boolean;
    court: boolean;
    fosterParent: boolean;
  };
  familyEcm: {
    yes: boolean;
    no: boolean;
    membersList: string;
  };

  // Section 2: Demographics

  demographics: {
    primaryContact: { cyMember: boolean; parentGuardian: boolean; other: boolean; otherList: string };
    speakingWith: { cyMember: boolean; parentGuardian: boolean; other: boolean; otherList: string };
    todayDate: string;
    cyMemberName: string;
    dateOfBirth: string;
    mediCalId: string;
    preferredName: string;
    genderIdentification: string;
    languageCy: string;
    languageParent: string;
    interpreterNeeded: { yes: boolean; no: boolean; language: string };
    culturalBeliefs: { yes: boolean; no: boolean; declined: boolean; describe: string };
    relationshipCy: {
      na: boolean; single: boolean; married: boolean; divorced: boolean;
      domesticPartnership: boolean; widower: boolean; other: boolean; otherText: string; declined: boolean;
    };
    relationshipParent: {
      na: boolean; single: boolean; married: boolean; divorced: boolean;
      domesticPartnership: boolean; widower: boolean; other: boolean; otherText: string; declined: boolean;
    };
    parentName: string;
    parentContact: string;
    custody: {
      biological: boolean; adoptive: boolean; foster: boolean; guardian: boolean;
      courtAppointed: boolean; jointLegal: boolean; soleLegal: boolean;
      jointPhysical: boolean; solePhysical: boolean; unaccompanied: boolean;
      refugee: boolean; asylumSeeker: boolean; emancipatedMinor: boolean;
    };
    ethnicity: {
      hispanicLatino: boolean; asian: boolean; pacificIslander: boolean;
      white: boolean; blackAfricanAmerican: boolean; americanIndian: boolean;
      other: boolean; otherText: string;
    };
    educationCy: {
      elementary: boolean; juniorHigh: boolean; highSchool: boolean;
      someCollege: boolean; collegeCompleted: boolean; technical: boolean;
      other: boolean; otherText: string; na: boolean;
    };
    educationParent: {
      elementary: boolean; juniorHigh: boolean; highSchool: boolean;
      someCollege: boolean; collegeCompleted: boolean; technical: boolean;
      other: boolean; otherText: string; na: boolean;
    };
    caregiverAssistance: { yes: boolean; no: boolean; info: string };
    ihss: { yes: boolean; no: boolean; info: string };
    needCaregiver: { yes: boolean; no: boolean; explain: string };
    caregiverNeedsHelp: { yes: boolean; no: boolean; na: boolean; declined: boolean; explain: string };
    additionalFamily: { yes: boolean; no: boolean; na: boolean; declined: boolean; list: string };
    employment: {
      yes: boolean;
      no: boolean;
      na: boolean;
      declined: boolean;
      list: string;
      partTime: boolean;
      fullTime: boolean;
      dayLaborer: boolean;
    };
    mostImportantNeed: string;
  };

  // Section 2 continued: Contact Information
  contactInfo: {
    address: string;
    city: string;
    state: string;
    zip: string;
    preferredMail: string;
    phone: string;
    homePhone: string;
    cellPhone: string;
    email: string;
    preferredContact: { phone: boolean; text: boolean; email: boolean; mail: boolean; inPerson: boolean };
    bestTimeToReach: string;
  };
  emergencyContact: {
    name: string;
    relationship: string;
    phone: string;
    address: string;
  };

  // Section 3: Health Literacy
  healthLiteracy: {
    understandHealthInfo: { confidently: boolean; somewhat: boolean; notAtAll: boolean; declined: boolean };
    fillOutForms: { confidently: boolean; somewhat: boolean; notAtAll: boolean; declined: boolean };
    helpWithForms: { yes: boolean; no: boolean; declined: boolean; explain: string };
  };

  // Section 4: Physical Health
  physicalHealth: {
    currentConditions: string;
    newDiagnoses: { yes: boolean; no: boolean; declined: boolean; list: string };
    hospitalizationsER: { yes: boolean; no: boolean; declined: boolean; list: string };
    allergies: { yes: boolean; no: boolean; declined: boolean; list: string };

    // Vision
    vision: { yes: boolean; no: boolean; describe: string };
    glasses: { yes: boolean; no: boolean; need: boolean };
    tty: { yes: boolean; no: boolean; need: boolean };
    visionOther: string;
    diabeticEye: { yes: boolean; no: boolean; na: boolean };

    // Hearing
    hearing: { yes: boolean; no: boolean; describe: string };
    hearingDevice: { yes: boolean; no: boolean; need: boolean; list: string };

    // Health Rating
    healthRating: {
      excellent: boolean;
      veryGood: boolean;
      good: boolean;
      fair: boolean;
      poor: boolean;
      declined: boolean;
    };
    healthRatingReason: string;

    // Hospital/ER Visits
    hospital: { yes: boolean; no: boolean; na: boolean; declined: boolean };
    hospitalDetails: string;

    // PCP
    pcpExists: { yes: boolean; no: boolean };
    pcp: { name: string; phone: string; address: string; lastVisit: string };
    pcpLastVisitPurpose: string;
    pcpLastVisitDate: string;

    // Dentist
    dentistExists: { yes: boolean; no: boolean };
    dentist: { name: string; phone: string; address: string; lastVisit: string };
    dentistLastVisitPurpose: string;
    dentistLastVisitDate: string;
    dentalIssues: { yes: boolean; no: boolean; na: boolean; declined: boolean };

    // Specialists (checkbox types)
    specialistTypes: {
      cardiology: boolean;
      developmental: boolean;
      endocrinology: boolean;
      genetics: boolean;
      hematology: boolean;
      immunology: boolean;
      neurology: boolean;
      oncology: boolean;
      orthopedics: boolean;
      pulmonology: boolean;
      respite: boolean;
      physicalTherapy: boolean;
      occupationalTherapy: boolean;
      speechTherapy: boolean;
      feedingTherapy: boolean;
      other: boolean;
      otherList: string;
    };
    specialistsContactInfo: string;
    specialists: Array<{ name: string; specialty: string; phone: string; lastVisit: string }>;

    // Medications
    medications: Array<{ name: string; dose: string; frequency: string; prescriber: string; purpose: string }>;
    medDifficulty: { yes: boolean; no: boolean };
    medDifficultyExplain: string;
    medMissed: { yes: boolean; no: boolean };
    medMissedExplain: string;
    adherence: { yes: boolean; no: boolean; sometimes: boolean; declined: boolean; explain: string };

    // Pain Assessment
    pain: { yes: boolean; no: boolean; declined: boolean };
    painInterference: {
      notAtAll: boolean;
      littleBit: boolean;
      moderately: boolean;
      quiteABit: boolean;
      extremely: boolean;
      declined: boolean;
    };
    painSupport: { yes: boolean; no: boolean; declined: boolean };
    painSupportDescribe: string;
    painManagement: { yes: boolean; no: boolean; declined: boolean };
    painProvider: { name: string; phone: string; address: string; lastVisit: string };
  };

  // Section 5: Pregnancy/Postpartum
  pregnancyPostpartum: {
    applicable: boolean;
    notReviewedChild: boolean;
    notReviewedOther: boolean;
    notReviewedReason: string;

    // Pregnancy Status
    currentlyPregnant: { yes: boolean; no: boolean; declined: boolean };
    weeksPregnant: string;
    discoveredPregnancy: { yes: boolean; no: boolean; na: boolean };
    dueDate: string;

    // OB/Midwife
    obMidwife: { yes: boolean; no: boolean };
    obMidwifeName: string;
    obMidwifeContact: string;
    obMidwifeLastVisit: string;
    prenatalProvider: { name: string; phone: string; address: string; lastVisit: string };

    // Planning
    planDelivery: { yes: boolean; no: boolean; na: boolean };
    planBreastfeed: { yes: boolean; no: boolean; na: boolean };
    planDoula: { yes: boolean; no: boolean };

    // Baby Essentials
    babyEssentials: {
      crib: boolean;
      carSeat: boolean;
      diapers: boolean;
      formula: boolean;
      breastPump: boolean;
      other: boolean;
      otherList: string;
    };

    // Classes and Support
    birthingClasses: { yes: boolean; no: boolean; interested: boolean };
    postpartumDoula: { yes: boolean; no: boolean; interested: boolean };
    lactationConsultant: { yes: boolean; no: boolean; interested: boolean };

    // Postpartum
    postpartumVisitCompleted: { yes: boolean; no: boolean; na: boolean };
    postpartumVisitDate: string;
    babyPediatrician: { name: string; phone: string; lastVisit: string };
  };

  // Section 6: Activities of Daily Living (ADLs)
  adls: {
    ageGroup: 'infant' | 'toddler' | 'preschool' | 'schoolAge' | 'adolescent' | 'adult';
    infantToddler: {
      feedingSucking: { independent: boolean; needsSupport: boolean; na: boolean };
      sleeping: { independent: boolean; needsSupport: boolean; na: boolean };
      motorDevelopment: { independent: boolean; needsSupport: boolean; na: boolean };
    };
    preschool: {
      dressing: { independent: boolean; needsSupport: boolean; na: boolean };
      toileting: { independent: boolean; needsSupport: boolean; na: boolean };
      feeding: { independent: boolean; needsSupport: boolean; na: boolean };
      socialPlay: { independent: boolean; needsSupport: boolean; na: boolean };
    };
    schoolAge: {
      hygiene: { independent: boolean; needsSupport: boolean; na: boolean };
      homework: { independent: boolean; needsSupport: boolean; na: boolean };
      peerInteraction: { independent: boolean; needsSupport: boolean; na: boolean };
      chores: { independent: boolean; needsSupport: boolean; na: boolean };
    };
    adolescent: {
      personalCare: { independent: boolean; needsSupport: boolean; na: boolean };
      timeManagement: { independent: boolean; needsSupport: boolean; na: boolean };
      healthManagement: { independent: boolean; needsSupport: boolean; na: boolean };
      transportation: { independent: boolean; needsSupport: boolean; na: boolean };
    };
    adultTransition: {
      cooking: { independent: boolean; needsSupport: boolean; na: boolean };
      financialManagement: { independent: boolean; needsSupport: boolean; na: boolean };
      employment: { independent: boolean; needsSupport: boolean; na: boolean };
      independentLiving: { independent: boolean; needsSupport: boolean; na: boolean };
    };
    supportNeeded: { yes: boolean; no: boolean; declined: boolean; explain: string };
    medicalEquipment: { yes: boolean; no: boolean; declined: boolean; list: string };
  };

  // Section 7: Psychosocial/Mental/Behavioral Health
  mentalHealth: {
    // Mental Health Diagnosis Question
    mhDiagnosis: { yes: boolean; no: boolean; declined: boolean; na: boolean };

    // Diagnosis Types
    diagnosis: {
      depression: boolean;
      bipolar: boolean;
      psychotic: boolean;
      anxiety: boolean;
      eating: boolean;
      other: boolean;
      otherList: string;
    };
    diagnosisComments: string;

    // Mental Health Provider
    mhProvider: { yes: boolean; no: boolean; na: boolean; declined: boolean };
    provider: { name: string; contact: string; address: string; lastVisit: string };

    // Social Interactions
    social: {
      lessOnce: boolean;
      oneTwo: boolean;
      threeFive: boolean;
      fiveMore: boolean;
      na: boolean;
      declined: boolean;
    };

    // Loneliness Scale
    lonely: {
      none: boolean;
      lessFive: boolean;
      moreHalf: boolean;
      most: boolean;
      na: boolean;
      declined: boolean;
    };

    // Parenting Programs
    parenting: { yes: boolean; no: boolean; declined: boolean };

    // Behavior/Mood Concerns
    behaviorConcerns: { yes: boolean; no: boolean; na: boolean; declined: boolean };
    behaviorConcernsDescribe: string;

    // Mental Health Support Requested
    mhSupportRequested: string;

    // PHQ-9 (for youth 11+)
    phq9: {
      score: string;
      date: string;
      na: boolean;
      declined: boolean;
      declinedReason: string;
    };

    // PHQ-2 + Q.9 Questionnaire
    phqQ1: { notAtAll: boolean; severalDays: boolean; moreHalf: boolean; nearlyEvery: boolean };
    phqQ2: { notAtAll: boolean; severalDays: boolean; moreHalf: boolean; nearlyEvery: boolean };
    phqQ9: { notAtAll: boolean; severalDays: boolean; moreHalf: boolean; nearlyEvery: boolean };
    phqActions: string;

    // Legacy fields (for backward compatibility)
    currentlyReceiving?: { yes: boolean; no: boolean; declined: boolean };
    diagnoses?: string;
    phq9Score?: string;
    phq9Date?: string;
    safetyPlan?: { yes: boolean; no: boolean; na: boolean };
    suicidalIdeation?: { yes: boolean; no: boolean; declined: boolean };
    selfHarm?: { yes: boolean; no: boolean; declined: boolean };
    traumaHistory?: { yes: boolean; no: boolean; declined: boolean };
    copingStrategies?: string;
    supportSystem?: string;
  };

  // Section 8: Substance Use
  substanceUse: {
    currentUse: { yes: boolean; no: boolean; declined: boolean };
    substances: Array<{
      type: string;
      frequency: string;
      lastUse: string;
      route: string;
    }>;
    treatmentHistory: { yes: boolean; no: boolean; na: boolean };
    treatmentDetails: string;
    interestInTreatment: { yes: boolean; no: boolean; na: boolean };
    narcanAccess: { yes: boolean; no: boolean; na: boolean };
    exposureConcerns: { yes: boolean; no: boolean; declined: boolean; describe: string };
  };

  // Section 9: Developmental and Cognitive Functioning
  developmentalCognitive: {
    developmentalDelays: { yes: boolean; no: boolean; declined: boolean; describe: string };
    iep504Plan: { yes: boolean; no: boolean; na: boolean };
    specialEducation: { yes: boolean; no: boolean; na: boolean };
    regionalCenterServices: { yes: boolean; no: boolean; na: boolean };
    educationalOpportunities: { yes: boolean; no: boolean; na: boolean; describe: string };
    cognitiveConcerns18Plus: { yes: boolean; no: boolean; na: boolean; describe: string };
  };

  // Section 10: Social Determinants of Health
  sdoh: {
    housing: {
      currentSituation: string;
      stable: { yes: boolean; no: boolean; declined: boolean };
      concerns: string;
    };
    environmentalSafety: {
      firearmAccess: { yes: boolean; no: boolean; declined: boolean };
      homeSafetyChecklist: {
        adequateLighting: boolean;
        adequateHeating: boolean;
        adequateCooling: boolean;
        safetyRails: boolean;
        hotWater: boolean;
        workingToilet: boolean;
        workingLocks: boolean;
        safeStairs: boolean;
        elevatorAccess: boolean;
        wheelchairAccessible: boolean;
        emergencyExits: boolean;
        leadPaintConcerns: boolean;
        moldMildew: boolean;
        overcrowding: boolean;
        utilitiesPaid: boolean;
        pestInfestation: boolean;
        additionalConcerns: string;
      };
    };
  };

  // Section 11: Benefits, Services, Access to Necessities
  benefitsServices: {
    currentBenefits: {
      calFresh: boolean;
      wic: boolean;
      tanf: boolean;
      ssiSsdi: boolean;
    };
    benefitNeeds: string;
    otherServices: string;
    moneyRunOut: { yes: boolean; no: boolean; declined: boolean };
    transportationBarriers: { yes: boolean; no: boolean; declined: boolean; list: string };
    childcareBarriers: { yes: boolean; no: boolean; declined: boolean; list: string };
  };

  // Section 12: Legal Involvement
  legalInvolvement: {
    past12Months: {
      courtOrderedServices: boolean;
      probation: boolean;
      parole: boolean;
      reentryProgram: boolean;
      duiRestricted: boolean;
      aps: boolean;
      cps: boolean;
      communityLegal: boolean;
      none: boolean;
      other: boolean;
      otherList: string;
    };
    comments: string;
    reentryProvider: { yes: boolean; no: boolean; declined: boolean };
    providerInfo: { name: string; phone: string; address: string; lastVisit: string };
  };

  // Section 13: End-of-Life Planning (18+)
  endOfLifePlanning: {
    applicable: boolean;
    advanceDirective: { yes: boolean; no: boolean; declined: boolean };
    wantsInformation: { yes: boolean; no: boolean; declined: boolean };
  };

  // Narrative Summary
  narrativeSummary: {
    primaryNeeds: string;
    nextSteps: Array<{ step: string; personResponsible: string }>;
    nextAppointmentLocation: string;
  };
}

// ============================================
// Partnership Consent Data (CS_CONSENT_-_Partnership.pdf)
// ============================================
export interface PartnershipConsentData {
  // Consent options (initials)
  consentInitial: string;
  declineInitial: string;

  // Special Authorization (initials)
  mentalHealthInitial: string;
  hivInitial: string;
  substanceUseInitial: string;

  // Expiration options (initials)
  expirationStandardInitial: string;
  expirationCustomInitial: string;
  customExpirationDate: string;

  // Representative information (if signing on behalf)
  repName: string;

  // Legacy fields for backward compatibility
  consentToShare: boolean;
  consentToCoordinate: boolean;
  consentToContact: boolean;
  organizationName: string;
  mentalHealthConsent: boolean;
  substanceUseConsent: boolean;
  hivConsent: boolean;
}

// ============================================
// Signature Data (used in all forms)
// ============================================
export type SignatureType = 'draw' | 'text' | 'upload';

export interface SignatureData {
  imageData: string; // base64 PNG
  signedBy: string;
  signedDate: string;
  relationship?: string; // Relationship to member (if signer is not the member)
  signatureType: SignatureType; // Method used: draw, text, or upload
  ipAddress?: string;
}

export type DocumentKey =
  | 'consent'
  | 'csConsentPartnership'
  | 'ecmRoi'
  | 'adultEcmAssessment'
  | 'cyEcmAssessment'
  | 'housingAssessment'
  | 'carePlan'
  | 'partnershipCarePlan'
  | 'ihsp'
  | 'phpCsReferral'
  | 'ecmReferralChildYouth'
  | 'ecmReferralAdult'
  | 'cchpEcmReferralChildYouth'
  | 'cchpEcmReferralAdult';

export type DocumentGroupKey =
  | 'contra_costa_adult'
  | 'contra_costa_children'
  | 'partnership_adult'
  | 'partnership_children';

export interface DocumentSelection {
  selectedGroup: DocumentGroupKey | '';
  individualDocuments: DocumentKey[];
  finalSelected?: DocumentKey[]; // For selective edit mode user modifications
}

// ============================================
// Main Form Submission
// ============================================
export type FlatAssessmentValue = string | number | boolean | undefined;
export type FlatAssessmentMap = Record<string, FlatAssessmentValue>;

export interface FormSubmission {
  // Submission metadata
  submissionId?: string;
  submittedAt?: string;
  submittedBy?: string;
  language?: 'en' | 'es'; // Language for PDF generation

  // Core member info (used in ALL forms)
  memberInfo: MemberInfo;

  // Form-specific data
  roi: ROIData;
  housing: HousingData;
  consent: ConsentData;
  carePlan: CarePlanData;
  partnershipConsent: PartnershipConsentData;
  cyEcmAssessment?: CYECMAssessmentData;

  // Flattened data for PDF generation
  cyEcmAssessment_flat?: FlatAssessmentMap;
  adultEcmAssessment_flat?: FlatAssessmentMap;
  partnershipCarePlan_flat?: Record<string, string | boolean>;
  ihsp_flat?: Record<string, string | boolean>;
  phpCsReferral_flat?: Record<string, string>;
  ecmReferralChildYouth_flat?: Record<string, string>;
  ecmReferralAdult_flat?: Record<string, string>;
  cchpEcmReferralChildYouth_flat?: Record<string, string>;
  cchpEcmReferralAdult_flat?: Record<string, string>;
  documentSelection?: DocumentSelection;
  selectedDocuments?: DocumentKey[];

  // Signature (used in ALL forms)
  signature: SignatureData;
}

// ============================================
// PDF Generation Types
// ============================================
export interface GeneratedPDF {
  formName: string;
  fileName: string;
  blobUrl: string;
  downloadUrl: string;
  generatedAt: string;
  pageCount: number;
}

export interface SubmissionResult {
  success: boolean;
  submissionId: string;
  generatedPDFs: GeneratedPDF[];
  errors?: string[];
}

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'staff';
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Form wizard step
export type FormStep =
  | 'document-selection'
  | 'member-info'
  | 'roi'
  | 'housing'
  | 'consent'
  | 'care-plan'
  | 'partnership'
  | 'partnership-care-plan'
  | 'ihsp'
  | 'php-cs-referral'
  | 'ecm-referral-child-youth'
  | 'ecm-referral-adult'
  | 'cchp-ecm-referral-child-youth'
  | 'cchp-ecm-referral-adult'
  | 'cy-ecm-assessment'
  | 'adult-ecm-assessment'
  | 'signature'
  | 'review';

export interface FormWizardStep {
  id: FormStep;
  title: string;
  description: string;
  isComplete: boolean;
}

// ============================================
// Default/Initial Values
// ============================================
export const getDefaultMemberInfo = (): MemberInfo => ({
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  medicalId: '',
  phone: '',
  email: '',
  address: {
    street: '',
    city: '',
    state: 'CA',
    zipCode: '',
  },
});

export const getDefaultROIData = (): ROIData => ({
  ecmProviderName: '',
  informationToRelease: {
    carePlans: false,
    medicalHistory: false,
    medications: false,
    mentalHealth: false,
    substanceUse: false,
    hivAids: false,
    hospitalizationER: false,
    socialServices: false,
    billing: false,
    other: false,
    otherDescription: '',
  },
  providerFrom: {
    name: 'California Support Services 420 Railroad Ave. Suite #103, Pittsburg, CA 94565',
    address: '',
    phone: '',
    fax: '',
  },
  providerTo: {
    name: 'California Support Services 420 Railroad Ave. Suite #103, Pittsburg, CA 94565',
    address: '',
    phone: '',
    fax: '',
  },
  purposeOfDisclosure: {
    continuityOfCare: false,
    careCoordination: false,
    ecmServiceDelivery: false,
    carePlanning: false,
    eligibility: false,
    other: false,
    otherDescription: '',
  },
  expirationOption: '',
  specificExpirationDate: '',
  mentalHealthInitial: '',
  sudInitial: '',
  hivInitial: '',
  witnessName: '',
  witnessDate: '',
});

export const getDefaultHousingData = (): HousingData => ({
  managedCarePlan: '',
  referralSource: {
    mcp: false,
    ecm: false,
    provider: false,
    self: false,
    other: false,
    otherDescription: '',
  },
  assessmentDate: '',
  assessorName: '',
  assessorTitle: '',
  assessorSignatureDate: '',
  currentSituation: '',
  currentSituationOther: '',
  lengthOfSituation: '',
  barriers: {
    lowIncome: false,
    poorCredit: false,
    priorEviction: false,
    criminalHistory: false,
    disability: false,
    behavioralHealth: false,
    lackDocuments: false,
    languageBarriers: false,
    transportationBarriers: false,
    financial: false,
    credit: false,
    rentalHistory: false,
    criminal: false,
    mentalHealth: false,
    substanceUse: false,
    family: false,
    documentation: false,
    other: false,
    otherDescription: '',
  },
  healthRelatedNeeds: '',
  relevantDiagnoses: '',
  howHousingHelps: '',
  diagnoses: [],
  primaryDiagnosis: '',
  navigation: {
    housingSearch: false,
    applications: false,
    documents: false,
    accommodation: false,
    landlordEngagement: false,
    leaseTerms: false,
    moveIn: false,
    rentalAssistance: false,
  },
  preferences: {
    locationCity: '',
    unitStudio: false,
    unit1BR: false,
    unit2BR: false,
    unitOther: false,
    unitOtherDescription: '',
    accessibilityNone: false,
    accessibilityADA: false,
    accessibilityOther: false,
    accessibilityOtherDescription: '',
  },
  income: {
    ssiSsdi: false,
    employment: false,
    ga: false,
    none: false,
    other: false,
    otherDescription: '',
  },
  sustaining: {
    newLease: false,
    existingLease: false,
    informal: false,
  },
  riskFactors: {
    rentUtilities: false,
    leaseViolations: false,
    conflict: false,
    maintainingUnit: false,
    missedAppointments: false,
    behavioralSymptoms: false,
    lackTenancySkills: false,
  },
  supportNeeds: {
    tenantRights: false,
    budgeting: false,
    utilitySetup: false,
    landlordComm: false,
    crisisPrevention: false,
    ongoingServices: false,
  },
  safety: {
    safetyConcernsYes: false,
    safetyConcernsNo: false,
    evictionRisk30Yes: false,
    evictionRisk30No: false,
    repeatedLossYes: false,
    repeatedLossNo: false,
    mitigationPlan: '',
  },
  memberGoals: '',
  housingGoals: '',
  shortTermGoals: '',
  longTermGoals: '',
  recommendation: {
    housingNavigation: false,
    housingSustaining: false,
    both: false,
    durationFrequency: '',
    rationale: '',
  },
  previousEvictions: false,
  evictionDetails: '',
  assessorSignature: '',
});

export const getDefaultConsentData = (): ConsentData => ({
  isMinor: false,
  signingOnBehalf: false,
  printedName: '',
  relationshipToMember: '',
});

export const getDefaultCarePlanData = (): CarePlanData => ({
  ecmProvider: {
    name: '',
    npi: '',
    phone: '',
    address: '',
  },
  leadCareManager: {
    name: '',
    phone: '',
    email: '',
  },
  primaryCareProvider: {
    name: '',
    phone: '',
    address: '',
  },
  ecmTargetGraduationDate: '',
  postGraduationSupports: '',
  communitySupportsReferral: false,
  communitySupportsDetails: '',
  dsnpDualEligible: false,
  dsnpHealthPlan: '',
  memberComments: '',
  actualGraduationDate: '',
  planStartDate: '',
  planReviewDate: '',
  problems: [],
  memberParticipation: '',
  memberStrengths: '',
  memberPreferences: '',
  emergencyContact: {
    name: '',
    relationship: '',
    phone: '',
  },
});

export const getDefaultPartnershipConsentData = (): PartnershipConsentData => ({
  consentInitial: '',
  declineInitial: '',
  mentalHealthInitial: '',
  hivInitial: '',
  substanceUseInitial: '',
  expirationStandardInitial: '',
  expirationCustomInitial: '',
  customExpirationDate: '',
  repName: '',
  consentToShare: false,
  consentToCoordinate: false,
  consentToContact: false,
  organizationName: '',
  mentalHealthConsent: false,
  substanceUseConsent: false,
  hivConsent: false,
});

export const getDefaultCYECMAssessmentData = (): CYECMAssessmentData => ({
  populationOfFocus: {
    homeless: false, atRiskED: false, smiSud: false, youthCorrectional: false,
    ccsCcsWcm: false, childWelfare: false, idd: false, pregnantPostpartum: false,
  },
  programs: {
    smhs: false, dmc: false, dmcOds: false, juvenileJustice: false, ccs: false,
    ccsWcm: false, childWelfare: false, regionalCenter: false, localProgram: false,
    localProgramList: '', other: false, otherList: '', na: false,
  },
  consent: {
    date: '', verbal: false, written: false, cyMember: false,
    parentGuardian: false, dcfs: false, court: false, fosterParent: false,
  },
  familyEcm: { yes: false, no: false, membersList: '' },
  assessmentTools: {
    aces: { used: false, yes: false, date: '', no: false, na: false },
    cans: { used: false, yes: false, date: '', no: false, na: false },
    psc: { used: false, yes: false, date: '', no: false, na: false },
    needs: { used: false, yes: false, date: '', no: false, na: false },
    youth: { used: false, yes: false, date: '', no: false, na: false },
    dph: { used: false, yes: false, date: '', no: false, na: false },
    protective: { used: false, yes: false, date: '', no: false, na: false },
    dcfs: { used: false, yes: false, date: '', no: false, na: false },
    ccsPatient: { used: false, yes: false, date: '', no: false, na: false },
    dds: { used: false, yes: false, date: '', no: false, na: false },
    cpsp: { used: false, yes: false, date: '', no: false, na: false },
    justice: { used: false, yes: false, date: '', no: false, na: false },
    other: false, otherList: '',
  },
  demographics: {
    primaryContact: { cyMember: false, parentGuardian: false, other: false, otherList: '' },
    speakingWith: { cyMember: false, parentGuardian: false, other: false, otherList: '' },
    todayDate: '', cyMemberName: '', dateOfBirth: '', mediCalId: '',
    preferredName: '', genderIdentification: '', languageCy: '', languageParent: '',
    interpreterNeeded: { yes: false, no: false, language: '' },
    culturalBeliefs: { yes: false, no: false, declined: false, describe: '' },
    relationshipCy: { na: false, single: false, married: false, divorced: false, domesticPartnership: false, widower: false, other: false, otherText: '', declined: false },
    relationshipParent: { na: false, single: false, married: false, divorced: false, domesticPartnership: false, widower: false, other: false, otherText: '', declined: false },
    parentName: '', parentContact: '',
    custody: { biological: false, adoptive: false, foster: false, guardian: false, courtAppointed: false, jointLegal: false, soleLegal: false, jointPhysical: false, solePhysical: false, unaccompanied: false, refugee: false, asylumSeeker: false, emancipatedMinor: false },
    ethnicity: { hispanicLatino: false, asian: false, pacificIslander: false, white: false, blackAfricanAmerican: false, americanIndian: false, other: false, otherText: '' },
    educationCy: { elementary: false, juniorHigh: false, highSchool: false, someCollege: false, collegeCompleted: false, technical: false, other: false, otherText: '', na: false },
    educationParent: { elementary: false, juniorHigh: false, highSchool: false, someCollege: false, collegeCompleted: false, technical: false, other: false, otherText: '', na: false },
    caregiverAssistance: { yes: false, no: false, info: '' },
    ihss: { yes: false, no: false, info: '' },
    needCaregiver: { yes: false, no: false, explain: '' },
    caregiverNeedsHelp: { yes: false, no: false, na: false, declined: false, explain: '' },
    additionalFamily: { yes: false, no: false, na: false, declined: false, list: '' },
    employment: { yes: false, no: false, na: false, declined: false, list: '', partTime: false, fullTime: false, dayLaborer: false },
    mostImportantNeed: '',
  },
  contactInfo: {
    address: '', city: '', state: 'CA', zip: '', preferredMail: '', phone: '', homePhone: '', cellPhone: '', email: '',
    preferredContact: { phone: false, text: false, email: false, mail: false, inPerson: false },
    bestTimeToReach: '',
  },
  emergencyContact: { name: '', relationship: '', phone: '', address: '' },
  healthLiteracy: {
    understandHealthInfo: { confidently: false, somewhat: false, notAtAll: false, declined: false },
    fillOutForms: { confidently: false, somewhat: false, notAtAll: false, declined: false },
    helpWithForms: { yes: false, no: false, declined: false, explain: '' },
  },
  physicalHealth: {
    currentConditions: '',
    newDiagnoses: { yes: false, no: false, declined: false, list: '' },
    hospitalizationsER: { yes: false, no: false, declined: false, list: '' },
    allergies: { yes: false, no: false, declined: false, list: '' },
    vision: { yes: false, no: false, describe: '' },
    glasses: { yes: false, no: false, need: false },
    tty: { yes: false, no: false, need: false },
    visionOther: '',
    diabeticEye: { yes: false, no: false, na: false },
    hearing: { yes: false, no: false, describe: '' },
    hearingDevice: { yes: false, no: false, need: false, list: '' },
    healthRating: { excellent: false, veryGood: false, good: false, fair: false, poor: false, declined: false },
    healthRatingReason: '',
    hospital: { yes: false, no: false, na: false, declined: false },
    hospitalDetails: '',
    pcpExists: { yes: false, no: false },
    pcp: { name: '', phone: '', address: '', lastVisit: '' },
    pcpLastVisitPurpose: '',
    pcpLastVisitDate: '',
    dentistExists: { yes: false, no: false },
    dentist: { name: '', phone: '', address: '', lastVisit: '' },
    dentistLastVisitPurpose: '',
    dentistLastVisitDate: '',
    dentalIssues: { yes: false, no: false, na: false, declined: false },
    specialistTypes: { cardiology: false, developmental: false, endocrinology: false, genetics: false, hematology: false, immunology: false, neurology: false, oncology: false, orthopedics: false, pulmonology: false, respite: false, physicalTherapy: false, occupationalTherapy: false, speechTherapy: false, feedingTherapy: false, other: false, otherList: '' },
    specialistsContactInfo: '',
    specialists: [],
    medications: [],
    medDifficulty: { yes: false, no: false },
    medDifficultyExplain: '',
    medMissed: { yes: false, no: false },
    medMissedExplain: '',
    adherence: { yes: false, no: false, sometimes: false, declined: false, explain: '' },
    pain: { yes: false, no: false, declined: false },
    painInterference: { notAtAll: false, littleBit: false, moderately: false, quiteABit: false, extremely: false, declined: false },
    painSupport: { yes: false, no: false, declined: false },
    painSupportDescribe: '',
    painManagement: { yes: false, no: false, declined: false },
    painProvider: { name: '', phone: '', address: '', lastVisit: '' },
  },
  pregnancyPostpartum: {
    applicable: false,
    notReviewedChild: false,
    notReviewedOther: false,
    notReviewedReason: '',
    currentlyPregnant: { yes: false, no: false, declined: false },
    weeksPregnant: '',
    discoveredPregnancy: { yes: false, no: false, na: false },
    dueDate: '',
    obMidwife: { yes: false, no: false },
    obMidwifeName: '',
    obMidwifeContact: '',
    obMidwifeLastVisit: '',
    prenatalProvider: { name: '', phone: '', address: '', lastVisit: '' },
    planDelivery: { yes: false, no: false, na: false },
    planBreastfeed: { yes: false, no: false, na: false },
    planDoula: { yes: false, no: false },
    babyEssentials: { crib: false, carSeat: false, diapers: false, formula: false, breastPump: false, other: false, otherList: '' },
    birthingClasses: { yes: false, no: false, interested: false },
    postpartumDoula: { yes: false, no: false, interested: false },
    lactationConsultant: { yes: false, no: false, interested: false },
    postpartumVisitCompleted: { yes: false, no: false, na: false },
    postpartumVisitDate: '',
    babyPediatrician: { name: '', phone: '', lastVisit: '' },
  },
  adls: {
    ageGroup: 'schoolAge',
    infantToddler: {
      feedingSucking: { independent: false, needsSupport: false, na: false },
      sleeping: { independent: false, needsSupport: false, na: false },
      motorDevelopment: { independent: false, needsSupport: false, na: false },
    },
    preschool: {
      dressing: { independent: false, needsSupport: false, na: false },
      toileting: { independent: false, needsSupport: false, na: false },
      feeding: { independent: false, needsSupport: false, na: false },
      socialPlay: { independent: false, needsSupport: false, na: false },
    },
    schoolAge: {
      hygiene: { independent: false, needsSupport: false, na: false },
      homework: { independent: false, needsSupport: false, na: false },
      peerInteraction: { independent: false, needsSupport: false, na: false },
      chores: { independent: false, needsSupport: false, na: false },
    },
    adolescent: {
      personalCare: { independent: false, needsSupport: false, na: false },
      timeManagement: { independent: false, needsSupport: false, na: false },
      healthManagement: { independent: false, needsSupport: false, na: false },
      transportation: { independent: false, needsSupport: false, na: false },
    },
    adultTransition: {
      cooking: { independent: false, needsSupport: false, na: false },
      financialManagement: { independent: false, needsSupport: false, na: false },
      employment: { independent: false, needsSupport: false, na: false },
      independentLiving: { independent: false, needsSupport: false, na: false },
    },
    supportNeeded: { yes: false, no: false, declined: false, explain: '' },
    medicalEquipment: { yes: false, no: false, declined: false, list: '' },
  },
  mentalHealth: {
    // Mental Health Diagnosis
    mhDiagnosis: { yes: false, no: false, declined: false, na: false },
    diagnosis: { depression: false, bipolar: false, psychotic: false, anxiety: false, eating: false, other: false, otherList: '' },
    diagnosisComments: '',
    // Mental Health Provider
    mhProvider: { yes: false, no: false, na: false, declined: false },
    provider: { name: '', contact: '', address: '', lastVisit: '' },
    // Social Interactions
    social: { lessOnce: false, oneTwo: false, threeFive: false, fiveMore: false, na: false, declined: false },
    // Loneliness Scale
    lonely: { none: false, lessFive: false, moreHalf: false, most: false, na: false, declined: false },
    // Parenting Programs
    parenting: { yes: false, no: false, declined: false },
    // Behavior Concerns
    behaviorConcerns: { yes: false, no: false, na: false, declined: false },
    behaviorConcernsDescribe: '',
    // Mental Health Support
    mhSupportRequested: '',
    // PHQ-9
    phq9: { score: '', date: '', na: false, declined: false, declinedReason: '' },
    phqQ1: { notAtAll: false, severalDays: false, moreHalf: false, nearlyEvery: false },
    phqQ2: { notAtAll: false, severalDays: false, moreHalf: false, nearlyEvery: false },
    phqQ9: { notAtAll: false, severalDays: false, moreHalf: false, nearlyEvery: false },
    phqActions: '',
    // Legacy fields
    currentlyReceiving: { yes: false, no: false, declined: false },
    diagnoses: '', phq9Score: '', phq9Date: '',
    safetyPlan: { yes: false, no: false, na: false },
    suicidalIdeation: { yes: false, no: false, declined: false },
    selfHarm: { yes: false, no: false, declined: false },
    traumaHistory: { yes: false, no: false, declined: false },
    copingStrategies: '', supportSystem: '',
  },
  substanceUse: {
    currentUse: { yes: false, no: false, declined: false },
    substances: [],
    treatmentHistory: { yes: false, no: false, na: false },
    treatmentDetails: '',
    interestInTreatment: { yes: false, no: false, na: false },
    narcanAccess: { yes: false, no: false, na: false },
    exposureConcerns: { yes: false, no: false, declined: false, describe: '' },
  },
  developmentalCognitive: {
    developmentalDelays: { yes: false, no: false, declined: false, describe: '' },
    iep504Plan: { yes: false, no: false, na: false },
    specialEducation: { yes: false, no: false, na: false },
    regionalCenterServices: { yes: false, no: false, na: false },
    educationalOpportunities: { yes: false, no: false, na: false, describe: '' },
    cognitiveConcerns18Plus: { yes: false, no: false, na: false, describe: '' },
  },
  sdoh: {
    housing: { currentSituation: '', stable: { yes: false, no: false, declined: false }, concerns: '' },
    environmentalSafety: {
      firearmAccess: { yes: false, no: false, declined: false },
      homeSafetyChecklist: {
        adequateLighting: false, adequateHeating: false, adequateCooling: false,
        safetyRails: false, hotWater: false, workingToilet: false, workingLocks: false,
        safeStairs: false, elevatorAccess: false, wheelchairAccessible: false,
        emergencyExits: false, leadPaintConcerns: false, moldMildew: false,
        overcrowding: false, utilitiesPaid: false, pestInfestation: false, additionalConcerns: '',
      },
    },
  },
  benefitsServices: {
    currentBenefits: { calFresh: false, wic: false, tanf: false, ssiSsdi: false },
    benefitNeeds: '', otherServices: '',
    moneyRunOut: { yes: false, no: false, declined: false },
    transportationBarriers: { yes: false, no: false, declined: false, list: '' },
    childcareBarriers: { yes: false, no: false, declined: false, list: '' },
  },
  legalInvolvement: {
    past12Months: {
      courtOrderedServices: false, probation: false, parole: false,
      reentryProgram: false, duiRestricted: false, aps: false, cps: false,
      communityLegal: false, none: false, other: false, otherList: '',
    },
    comments: '',
    reentryProvider: { yes: false, no: false, declined: false },
    providerInfo: { name: '', phone: '', address: '', lastVisit: '' },
  },
  endOfLifePlanning: {
    applicable: false,
    advanceDirective: { yes: false, no: false, declined: false },
    wantsInformation: { yes: false, no: false, declined: false },
  },
  narrativeSummary: {
    primaryNeeds: '',
    nextSteps: [{ step: '', personResponsible: '' }, { step: '', personResponsible: '' }, { step: '', personResponsible: '' }],
    nextAppointmentLocation: '',
  },
});

export const getTestCYECMAssessmentData = (): CYECMAssessmentData => ({
  populationOfFocus: {
    homeless: true, atRiskED: false, smiSud: false, youthCorrectional: false,
    ccsCcsWcm: false, childWelfare: true, idd: false, pregnantPostpartum: false,
  },
  programs: {
    smhs: false, dmc: true, dmcOds: false, juvenileJustice: false, ccs: false,
    ccsWcm: false, childWelfare: true, regionalCenter: false, localProgram: false,
    localProgramList: '', other: false, otherList: '', na: false,
  },
  consent: {
    date: new Date().toISOString().split('T')[0], verbal: false, written: true, cyMember: false,
    parentGuardian: true, dcfs: false, court: false, fosterParent: false,
  },
  familyEcm: { yes: false, no: true, membersList: '' },
  assessmentTools: {
    aces: { used: true, yes: true, date: '2024-01-15', no: false, na: false },
    cans: { used: false, yes: false, date: '', no: false, na: false },
    psc: { used: false, yes: false, date: '', no: false, na: false },
    needs: { used: false, yes: false, date: '', no: false, na: false },
    youth: { used: false, yes: false, date: '', no: false, na: false },
    dph: { used: false, yes: false, date: '', no: false, na: false },
    protective: { used: false, yes: false, date: '', no: false, na: false },
    dcfs: { used: false, yes: false, date: '', no: false, na: false },
    ccsPatient: { used: false, yes: false, date: '', no: false, na: false },
    dds: { used: false, yes: false, date: '', no: false, na: false },
    cpsp: { used: false, yes: false, date: '', no: false, na: false },
    justice: { used: false, yes: false, date: '', no: false, na: false },
    other: false, otherList: '',
  },
  demographics: {
    primaryContact: { cyMember: false, parentGuardian: true, other: false, otherList: '' },
    speakingWith: { cyMember: false, parentGuardian: true, other: false, otherList: '' },
    todayDate: new Date().toISOString().split('T')[0], cyMemberName: 'Jane Doe', dateOfBirth: '2010-03-20', mediCalId: 'MC123456789',
    preferredName: 'Jane', genderIdentification: 'Female', languageCy: 'English', languageParent: 'English',
    interpreterNeeded: { yes: true, no: false, language: 'Spanish' },
    culturalBeliefs: { yes: true, no: false, declined: false, describe: 'Prefers female providers' },
    relationshipCy: { na: false, single: true, married: false, divorced: false, domesticPartnership: false, widower: false, other: false, otherText: '', declined: false },
    relationshipParent: { na: false, single: false, married: true, divorced: false, domesticPartnership: false, widower: false, other: false, otherText: '', declined: false },
    parentName: 'Mary Doe', parentContact: '(555) 987-6543',
    custody: { biological: true, adoptive: false, foster: false, guardian: false, courtAppointed: false, jointLegal: false, soleLegal: true, jointPhysical: false, solePhysical: true, unaccompanied: false, refugee: false, asylumSeeker: false, emancipatedMinor: false },
    ethnicity: { hispanicLatino: true, asian: false, pacificIslander: false, white: false, blackAfricanAmerican: false, americanIndian: true, other: false, otherText: '' },
    educationCy: { elementary: false, juniorHigh: true, highSchool: false, someCollege: false, collegeCompleted: false, technical: false, other: false, otherText: '', na: false },
    educationParent: { elementary: false, juniorHigh: false, highSchool: true, someCollege: false, collegeCompleted: false, technical: false, other: false, otherText: '', na: false },
    caregiverAssistance: { yes: true, no: false, info: 'Needs help with transportation' },
    ihss: { yes: true, no: false, info: 'Pending application' },
    needCaregiver: { yes: true, no: false, explain: 'Due to mobility issues' },
    caregiverNeedsHelp: { yes: true, no: false, na: false, declined: false, explain: 'Respite care needed' },
    additionalFamily: { yes: true, no: false, na: false, declined: false, list: 'Grandmother lives in home' },
    employment: { yes: true, no: false, na: false, declined: false, list: 'Works at grocery store', partTime: true, fullTime: false, dayLaborer: false },
    mostImportantNeed: 'Stable housing and mental health support',
  },
  contactInfo: {
    address: '123 Main Street', city: 'Los Angeles', state: 'CA', zip: '90001', preferredMail: '123 Main Street', phone: '(555) 123-4567', homePhone: '(555) 123-4567', cellPhone: '(555) 987-6543', email: 'mary.doe@example.com',
    preferredContact: { phone: true, text: true, email: false, mail: false, inPerson: false },
    bestTimeToReach: 'Evenings after 5 PM',
  },
  emergencyContact: { name: 'John Doe', relationship: 'Father', phone: '(555) 111-2222', address: '123 Main Street, Los Angeles, CA 90001' },
  healthLiteracy: {
    understandHealthInfo: { confidently: false, somewhat: true, notAtAll: false, declined: false },
    fillOutForms: { confidently: false, somewhat: true, notAtAll: false, declined: false },
    helpWithForms: { yes: true, no: false, declined: false, explain: 'Needs assistance with complex medical forms' },
  },
  physicalHealth: {
    currentConditions: 'Asthma, ADHD',
    newDiagnoses: { yes: true, no: false, declined: false, list: 'Diabetes Type 2' },
    hospitalizationsER: { yes: true, no: false, declined: false, list: 'ER visit for asthma attack in Jan' },
    allergies: { yes: true, no: false, declined: false, list: 'Penicillin, peanuts' },
    vision: { yes: true, no: false, describe: 'Nearsighted' },
    glasses: { yes: true, no: false, need: false },
    tty: { yes: false, no: false, need: true },
    visionOther: 'Reading glasses',
    diabeticEye: { yes: true, no: false, na: false },
    hearing: { yes: true, no: false, describe: 'Partial hearing loss in left ear' },
    hearingDevice: { yes: true, no: false, need: false, list: 'Hearing aid left ear' },
    healthRating: { excellent: false, veryGood: true, good: false, fair: false, poor: false, declined: false },
    healthRatingReason: 'Generally healthy but manages asthma well',
    hospital: { yes: true, no: false, na: false, declined: false },
    hospitalDetails: 'ER visit last month for asthma',
    pcpExists: { yes: true, no: false },
    pcp: { name: 'Dr. Smith', phone: '(555) 234-5678', address: '456 Medical Center, Los Angeles, CA 90002', lastVisit: '2024-01-10' },
    pcpLastVisitPurpose: 'Annual checkup',
    pcpLastVisitDate: '2024-01-10',
    dentistExists: { yes: true, no: false },
    dentist: { name: 'Dr. Johnson', phone: '(555) 345-6789', address: '789 Dental Clinic, Los Angeles, CA 90003', lastVisit: '2024-02-15' },
    dentistLastVisitPurpose: 'Cleaning',
    dentistLastVisitDate: '2024-02-15',
    dentalIssues: { yes: true, no: false, na: false, declined: false },
    specialistTypes: { cardiology: true, developmental: false, endocrinology: true, genetics: false, hematology: false, immunology: false, neurology: false, oncology: false, orthopedics: false, pulmonology: true, respite: false, physicalTherapy: false, occupationalTherapy: false, speechTherapy: false, feedingTherapy: false, other: true, otherList: 'Dermatologist' },
    specialistsContactInfo: 'Dr. Brown - Pulmonology - (555) 456-7890\nDr. Green - Endocrinology - (555) 111-2222',
    specialists: [{ name: 'Dr. Brown', specialty: 'Pulmonology', phone: '(555) 456-7890', lastVisit: '2024-01-20' }],
    medications: [
      { name: 'Albuterol', dose: '90 mcg', frequency: 'As needed', prescriber: 'Dr. Smith', purpose: 'Asthma' },
      { name: 'Methylphenidate', dose: '10 mg', frequency: 'Twice daily', prescriber: 'Dr. Wilson', purpose: 'ADHD' }
    ],
    medDifficulty: { yes: true, no: false },
    medDifficultyExplain: 'Pharmacy is far',
    medMissed: { yes: true, no: false },
    medMissedExplain: 'Forgot on weekend',
    adherence: { yes: false, no: false, sometimes: true, declined: false, explain: 'Sometimes forgets morning dose' },
    pain: { yes: true, no: false, declined: false },
    painInterference: { notAtAll: false, littleBit: true, moderately: false, quiteABit: false, extremely: false, declined: false },
    painSupport: { yes: true, no: false, declined: false },
    painSupportDescribe: 'Physical therapy',
    painManagement: { yes: true, no: false, declined: false },
    painProvider: { name: 'Dr. Pain', phone: '(555) 999-8888', address: '123 Pain Clinic', lastVisit: '2024-01-01' },
  },
  pregnancyPostpartum: {
    applicable: true,
    notReviewedChild: false,
    notReviewedOther: false,
    notReviewedReason: '',
    currentlyPregnant: { yes: true, no: false, declined: false },
    weeksPregnant: '24',
    discoveredPregnancy: { yes: true, no: false, na: false },
    dueDate: '2024-10-15',
    obMidwife: { yes: true, no: false },
    obMidwifeName: 'Dr. Baby',
    obMidwifeContact: '(555) 444-3333',
    obMidwifeLastVisit: '2024-05-01',
    prenatalProvider: { name: 'Dr. Baby', phone: '(555) 444-3333', address: '789 Baby Lane', lastVisit: '2024-05-01' },
    planDelivery: { yes: true, no: false, na: false },
    planBreastfeed: { yes: true, no: false, na: false },
    planDoula: { yes: true, no: false },
    babyEssentials: { crib: true, carSeat: true, diapers: false, formula: true, breastPump: false, other: true, otherList: 'Stroller' },
    birthingClasses: { yes: true, no: false, interested: false },
    postpartumDoula: { yes: false, no: false, interested: true },
    lactationConsultant: { yes: false, no: false, interested: true },
    postpartumVisitCompleted: { yes: false, no: true, na: false },
    postpartumVisitDate: '',
    babyPediatrician: { name: 'Dr. Kid', phone: '(555) 222-1111', lastVisit: '' },
  },
  adls: {
    ageGroup: 'schoolAge',
    infantToddler: {
      feedingSucking: { independent: false, needsSupport: true, na: false },
      sleeping: { independent: false, needsSupport: true, na: false },
      motorDevelopment: { independent: true, needsSupport: false, na: false },
    },
    preschool: {
      dressing: { independent: false, needsSupport: true, na: false },
      toileting: { independent: true, needsSupport: false, na: false },
      feeding: { independent: true, needsSupport: false, na: false },
      socialPlay: { independent: false, needsSupport: true, na: false },
    },
    schoolAge: {
      hygiene: { independent: false, needsSupport: true, na: false },
      homework: { independent: false, needsSupport: true, na: false },
      peerInteraction: { independent: true, needsSupport: false, na: false },
      chores: { independent: false, needsSupport: true, na: false },
    },
    adolescent: {
      personalCare: { independent: true, needsSupport: false, na: false },
      timeManagement: { independent: false, needsSupport: true, na: false },
      healthManagement: { independent: false, needsSupport: true, na: false },
      transportation: { independent: true, needsSupport: false, na: false },
    },
    adultTransition: {
      cooking: { independent: false, needsSupport: true, na: false },
      financialManagement: { independent: false, needsSupport: true, na: false },
      employment: { independent: true, needsSupport: false, na: false },
      independentLiving: { independent: false, needsSupport: true, na: false },
    },
    supportNeeded: { yes: true, no: false, declined: false, explain: 'Needs help with homework and hygiene routines' },
    medicalEquipment: { yes: true, no: false, declined: false, list: 'Nebulizer for asthma' },
  },
  mentalHealth: {
    // Mental Health Diagnosis
    mhDiagnosis: { yes: true, no: false, declined: false, na: false },
    diagnosis: { depression: false, bipolar: false, psychotic: false, anxiety: true, eating: false, other: true, otherList: 'ADHD' },
    diagnosisComments: 'Has difficulty focusing in school, experiences anxiety in social situations',
    // Mental Health Provider
    mhProvider: { yes: true, no: false, na: false, declined: false },
    provider: { name: 'Dr. Wilson', contact: '(555) 567-8901', address: '202 Mental Health Center, Los Angeles, CA 90005', lastVisit: '2024-01-25' },
    // Social Interactions
    social: { lessOnce: false, oneTwo: false, threeFive: true, fiveMore: false, na: false, declined: false },
    // Loneliness Scale
    lonely: { none: false, lessFive: true, moreHalf: false, most: false, na: false, declined: false },
    // Parenting Programs
    parenting: { yes: true, no: false, declined: false },
    // Behavior Concerns
    behaviorConcerns: { yes: true, no: false, na: false, declined: false },
    behaviorConcernsDescribe: 'Sometimes has difficulty regulating emotions when frustrated',
    // Mental Health Support
    mhSupportRequested: 'Would like information about coping skills groups for children',
    // PHQ-9
    phq9: { score: '12', date: '2024-01-25', na: false, declined: false, declinedReason: '' },
    phqQ1: { notAtAll: false, severalDays: true, moreHalf: false, nearlyEvery: false },
    phqQ2: { notAtAll: false, severalDays: true, moreHalf: false, nearlyEvery: false },
    phqQ9: { notAtAll: true, severalDays: false, moreHalf: false, nearlyEvery: false },
    phqActions: 'Consulted with clinical supervisor, referred for full PHQ-9 assessment',
    // Legacy fields
    currentlyReceiving: { yes: true, no: false, declined: false },
    diagnoses: 'ADHD, Anxiety', phq9Score: '12', phq9Date: '2024-01-25',
    safetyPlan: { yes: true, no: false, na: false },
    suicidalIdeation: { yes: false, no: true, declined: false },
    selfHarm: { yes: false, no: true, declined: false },
    traumaHistory: { yes: true, no: false, declined: false },
    copingStrategies: 'Deep breathing, talking to friends', supportSystem: 'Family, school counselor',
  },
  substanceUse: {
    currentUse: { yes: true, no: false, declined: false },
    substances: [
      { type: 'Marijuana', frequency: 'Weekly', lastUse: 'Last weekend', route: 'Smoking' }
    ],
    treatmentHistory: { yes: true, no: false, na: false },
    treatmentDetails: 'Completed outpatient program in 2023',
    interestInTreatment: { yes: true, no: false, na: false },
    narcanAccess: { yes: false, no: false, na: true },
    exposureConcerns: { yes: false, no: true, declined: false, describe: '' },
  },
  developmentalCognitive: {
    developmentalDelays: { yes: true, no: false, declined: false, describe: 'ADHD diagnosis affecting attention and executive functioning' },
    iep504Plan: { yes: true, no: false, na: false },
    specialEducation: { yes: true, no: false, na: false },
    regionalCenterServices: { yes: false, no: true, na: false },
    educationalOpportunities: { yes: true, no: false, na: false, describe: 'Interested in vocational training' },
    cognitiveConcerns18Plus: { yes: false, no: false, na: true, describe: '' },
  },
  sdoh: {
    housing: { currentSituation: 'stable', stable: { yes: true, no: false, declined: false }, concerns: '' },
    environmentalSafety: {
      firearmAccess: { yes: false, no: true, declined: false },
      homeSafetyChecklist: {
        adequateLighting: true, adequateHeating: true, adequateCooling: true,
        safetyRails: true, hotWater: true, workingToilet: true, workingLocks: true,
        safeStairs: true, elevatorAccess: false, wheelchairAccessible: false,
        emergencyExits: true, leadPaintConcerns: false, moldMildew: false,
        overcrowding: false, utilitiesPaid: true, pestInfestation: false, additionalConcerns: '',
      },
    },
  },
  benefitsServices: {
    currentBenefits: { calFresh: true, wic: false, tanf: false, ssiSsdi: false },
    benefitNeeds: 'Assistance with CalFresh renewal', otherServices: 'School lunch program',
    moneyRunOut: { yes: false, no: true, declined: false },
    transportationBarriers: { yes: true, no: false, declined: false, list: 'Does not own car' },
    childcareBarriers: { yes: false, no: true, declined: false, list: '' },
  },
  legalInvolvement: {
    past12Months: {
      courtOrderedServices: false, probation: false, parole: false,
      reentryProgram: false, duiRestricted: false, aps: false, cps: true,
      communityLegal: false, none: false, other: false, otherList: '',
    },
    comments: 'Ongoing CPS involvement for child welfare services',
    reentryProvider: { yes: false, no: true, declined: false },
    providerInfo: { name: '', phone: '', address: '', lastVisit: '' },
  },
  endOfLifePlanning: {
    applicable: true,
    advanceDirective: { yes: true, no: false, declined: false },
    wantsInformation: { yes: false, no: false, declined: false },
  },
  narrativeSummary: {
    primaryNeeds: 'Stable housing, mental health support, educational accommodations for ADHD',
    nextSteps: [
      { step: 'Schedule IEP meeting', personResponsible: 'School counselor' },
      { step: 'Follow up with mental health provider', personResponsible: 'Care manager' },
      { step: 'Assist with CalFresh renewal', personResponsible: 'Social worker' }
    ],
    nextAppointmentLocation: 'Downtown Medical Clinic',
  },
});

export const getDefaultSignatureData = (): SignatureData => ({
  imageData: '',
  signedBy: '',
  signedDate: '',
  relationship: '',
  signatureType: 'draw',
  ipAddress: '',
});

export const getDefaultFormSubmission = (): FormSubmission => ({
  memberInfo: getDefaultMemberInfo(),
  roi: getDefaultROIData(),
  housing: getDefaultHousingData(),
  consent: getDefaultConsentData(),
  carePlan: getDefaultCarePlanData(),
  partnershipConsent: getDefaultPartnershipConsentData(),
  cyEcmAssessment: getDefaultCYECMAssessmentData(),
  documentSelection: {
    selectedGroup: '',
    individualDocuments: [],
  },
  selectedDocuments: [],
  signature: getDefaultSignatureData(),
});
